import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Mail, 
  MessageSquare, 
  Bell, 
  Smartphone,
  Monitor,
  Share2,
  ArrowRight,
  ChevronRight,
  BarChart3,
  Users,
  Globe,
  Radio,
  Database,
  Cpu,
  CheckCircle2,
  Circle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

// Partner names for marquee - Trusted brands
const partners = [
  "Liberty Mutual", "LendingTree", "SoFi", "Experian", "LoanDepot", "T-Mobile", "Uber", "Lyft", "ADT"
];

// Stats data
const stats = [
  { value: "1B+", label: "Ad Interactions We Manage/Month" },
  { value: "500+", label: "Campaigns Executed" },
  { value: "54%", label: "Avg. Conversion Lift" }
];

// Channels data
const channels = [
  { icon: Mail, name: "Email" },
  { icon: Bell, name: "Push" },
  { icon: MessageSquare, name: "SMS" },
  { icon: Smartphone, name: "In-App" },
  { icon: Monitor, name: "Paid Media" },
  { icon: Globe, name: "Web" },
  { icon: Share2, name: "WhatsApp" },
  { icon: Radio, name: "Audio" }
];

// Case studies data with Goal → Results format
const caseStudies = [
  {
    slug: "personal-loans",
    category: "Personal Loans",
    goal: "Identify high-intent borrowers actively researching personal loan options and drive qualified applications while reducing cost-per-funded-loan.",
    result: "62% increase in funded loan volume with 41% reduction in cost-per-acquisition. Average loan size increased by $2,400.",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=500&h=300&fit=crop"
  },
  {
    slug: "credit-cards",
    category: "Credit Cards",
    goal: "Target consumers with strong credit profiles showing intent signals for premium rewards cards, maximizing approval rates and first-year spend.",
    result: "78% approval rate (vs. 52% benchmark), 34% higher first-year spend per cardholder, and 2.8x ROAS within 90 days.",
    image: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=500&h=300&fit=crop"
  },
  {
    slug: "credit-score-services",
    category: "Credit Score Services",
    goal: "Acquire credit-conscious consumers seeking credit monitoring and improvement tools through predictive audience targeting.",
    result: "156% lift in trial-to-paid conversion rate, 47% lower customer acquisition cost, and 3.2x increase in 12-month LTV.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=300&fit=crop"
  }
];

// ============================================
// HERO DATA VISUALIZATION
// ============================================

const HeroDataVisualization = () => {
  return (
    <div className="hero-data-viz">
      {/* Performance Chart */}
      <div className="viz-card viz-chart-card">
        <div className="viz-card-header">
          <span className="viz-card-title">Campaign Performance</span>
          <span className="viz-card-badge">Live</span>
        </div>
        <div className="viz-chart">
          <div className="viz-bars">
            <div className="viz-bar-group">
              <div className="viz-bar" style={{ height: '45%' }}></div>
              <span>Jan</span>
            </div>
            <div className="viz-bar-group">
              <div className="viz-bar" style={{ height: '62%' }}></div>
              <span>Feb</span>
            </div>
            <div className="viz-bar-group">
              <div className="viz-bar" style={{ height: '58%' }}></div>
              <span>Mar</span>
            </div>
            <div className="viz-bar-group">
              <div className="viz-bar viz-bar-highlight" style={{ height: '85%' }}></div>
              <span>Apr</span>
            </div>
            <div className="viz-bar-group">
              <div className="viz-bar" style={{ height: '78%' }}></div>
              <span>May</span>
            </div>
            <div className="viz-bar-group">
              <div className="viz-bar viz-bar-current" style={{ height: '92%' }}></div>
              <span>Jun</span>
            </div>
          </div>
        </div>
        <div className="viz-chart-footer">
          <span className="viz-metric-up">+34% vs last quarter</span>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="viz-metrics-row">
        <div className="viz-metric-card">
          <span className="viz-metric-value">245M+</span>
          <span className="viz-metric-label">Consumer Profiles</span>
        </div>
        <div className="viz-metric-card">
          <span className="viz-metric-value">47%</span>
          <span className="viz-metric-label">Lower CAC</span>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="viz-card viz-funnel-card">
        <div className="viz-card-header">
          <span className="viz-card-title">Conversion Funnel</span>
        </div>
        <div className="viz-funnel">
          <div className="viz-funnel-stage">
            <div className="viz-funnel-bar" style={{ width: '100%' }}></div>
            <div className="viz-funnel-info">
              <span>Impressions</span>
              <strong>2.4M</strong>
            </div>
          </div>
          <div className="viz-funnel-stage">
            <div className="viz-funnel-bar" style={{ width: '65%' }}></div>
            <div className="viz-funnel-info">
              <span>Engaged</span>
              <strong>1.56M</strong>
            </div>
          </div>
          <div className="viz-funnel-stage">
            <div className="viz-funnel-bar" style={{ width: '28%' }}></div>
            <div className="viz-funnel-info">
              <span>Converted</span>
              <strong>672K</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// HERO IMAGE COMPONENT (Legacy - kept for reference)
// ============================================

// Hero Image - Professional team working with data
const HeroImage = () => {
  return (
    <div className="hero-image-container" data-testid="hero-image">
      <div className="hero-image-wrapper">
        <img 
          src="/hero-image.jpg" 
          alt="Data science team analyzing marketing intelligence" 
          className="hero-image"
        />
        <div className="hero-image-overlay">
          {/* Floating metric badges */}
          <div className="hero-badge hero-badge-1">
            <span className="badge-value">AI</span>
            <span className="badge-label">Powered</span>
          </div>
          <div className="hero-badge hero-badge-2">
            <span className="badge-value">Data</span>
            <span className="badge-label">Driven</span>
          </div>
          <div className="hero-badge hero-badge-3">
            <span className="badge-value">Results</span>
            <span className="badge-label">Focused</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPLEX STATIC VISUALIZATIONS
// ============================================

// AI Signal Processing Visualization
const AISignalVisualization = () => {
  const signals = [
    { type: 'Behavioral', value: 2847, color: '#3b82f6' },
    { type: 'Transactional', value: 1923, color: '#10b981' },
    { type: 'Identity', value: 1456, color: '#8b5cf6' },
    { type: 'Intent', value: 987, color: '#f59e0b' },
  ];
  
  const maxValue = Math.max(...signals.map(s => s.value));

  return (
    <div className="viz-container signal-viz" data-testid="signal-visualization">
      <div className="viz-header">
        <span className="viz-label">SIGNAL PROCESSING</span>
        <span className="viz-status">LIVE</span>
      </div>
      
      <div className="signal-bars">
        {signals.map((signal, i) => (
          <div key={i} className="signal-bar-row">
            <span className="signal-type">{signal.type}</span>
            <div className="signal-bar-track">
              <div 
                className="signal-bar-fill" 
                style={{ 
                  width: `${(signal.value / maxValue) * 100}%`,
                  backgroundColor: signal.color
                }}
              />
            </div>
            <span className="signal-value">{signal.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
      
      <div className="signal-metrics">
        <div className="signal-metric">
          <Database size={14} />
          <span>5,000+ signals/user</span>
        </div>
        <div className="signal-metric">
          <Cpu size={14} />
          <span>Real-time processing</span>
        </div>
      </div>
      
      <svg viewBox="0 0 200 60" className="signal-flow-svg">
        <defs>
          <linearGradient id="flowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.1"/>
            <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
          </linearGradient>
        </defs>
        <path d="M0,30 Q50,10 100,30 T200,30" fill="none" stroke="url(#flowGrad)" strokeWidth="20"/>
        <path d="M0,30 Q50,10 100,30 T200,30" fill="none" stroke="#3b82f6" strokeWidth="1" strokeDasharray="4 2"/>
        
        {[20, 60, 100, 140, 180].map((x, i) => (
          <circle key={i} cx={x} cy={30 + Math.sin(x * 0.05) * 10} r="3" fill="#3b82f6"/>
        ))}
      </svg>
    </div>
  );
};

// Customer Profile Unification Visualization
const ProfileUnificationVisualization = () => {
  const dataSources = [
    { icon: Globe, label: 'Web' },
    { icon: Smartphone, label: 'App' },
    { icon: Mail, label: 'Email' },
    { icon: Database, label: 'CRM' },
  ];

  const profileAttributes = [
    { label: 'Demographics', pct: 95 },
    { label: 'Behavior', pct: 87 },
    { label: 'Preferences', pct: 92 },
    { label: 'Intent', pct: 78 },
  ];

  return (
    <div className="viz-container profile-viz" data-testid="profile-visualization">
      <div className="viz-header">
        <span className="viz-label">UNIFIED PROFILE</span>
        <span className="viz-id">#U-847291</span>
      </div>
      
      <div className="profile-grid">
        <div className="profile-sources">
          {dataSources.map((source, i) => (
            <div key={i} className="profile-source">
              <source.icon size={16} />
              <span>{source.label}</span>
              <CheckCircle2 size={12} className="check-icon" />
            </div>
          ))}
        </div>
        
        <div className="profile-center">
          <div className="profile-avatar">
            <Users size={24} />
          </div>
          <div className="profile-score">
            <span className="score-value">94</span>
            <span className="score-label">Completeness</span>
          </div>
        </div>
        
        <div className="profile-attributes">
          {profileAttributes.map((attr, i) => (
            <div key={i} className="profile-attr">
              <div className="attr-header">
                <span>{attr.label}</span>
                <span>{attr.pct}%</span>
              </div>
              <div className="attr-bar">
                <div className="attr-fill" style={{ width: `${attr.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="profile-footer">
        <div className="profile-stat">
          <span className="stat-num">245M+</span>
          <span className="stat-label">ID Graph</span>
        </div>
        <div className="profile-stat">
          <span className="stat-num">99.2%</span>
          <span className="stat-label">Match Rate</span>
        </div>
      </div>
    </div>
  );
};

// Cross-Channel Orchestration Visualization
const OrchestrationVisualization = () => {
  const journeySteps = [
    { channel: 'Email', status: 'sent', time: '0h' },
    { channel: 'Push', status: 'sent', time: '2h' },
    { channel: 'SMS', status: 'pending', time: '24h' },
    { channel: 'Ads', status: 'scheduled', time: '48h' },
  ];

  const metrics = [
    { label: 'Open Rate', value: '34.2%', trend: '+5.1%' },
    { label: 'CTR', value: '12.8%', trend: '+2.3%' },
    { label: 'Conversion', value: '4.7%', trend: '+1.2%' },
  ];

  return (
    <div className="viz-container orchestration-viz" data-testid="orchestration-visualization">
      <div className="viz-header">
        <span className="viz-label">JOURNEY ORCHESTRATION</span>
        <span className="viz-campaign">Campaign #1247</span>
      </div>
      
      <div className="journey-timeline">
        {journeySteps.map((step, i) => (
          <div key={i} className="journey-step">
            <div className={`step-node ${step.status}`}>
              {step.status === 'sent' && <CheckCircle2 size={14} />}
              {step.status === 'pending' && <Circle size={14} />}
              {step.status === 'scheduled' && <Circle size={14} />}
            </div>
            <span className="step-channel">{step.channel}</span>
            <span className="step-time">{step.time}</span>
            {i < journeySteps.length - 1 && <div className="step-connector" />}
          </div>
        ))}
      </div>
      
      <div className="orchestration-metrics">
        {metrics.map((metric, i) => (
          <div key={i} className="orch-metric">
            <span className="metric-value">{metric.value}</span>
            <span className="metric-label">{metric.label}</span>
            <span className="metric-trend positive">{metric.trend}</span>
          </div>
        ))}
      </div>
      
      <div className="channel-distribution">
        <div className="dist-bar">
          <div className="dist-segment" style={{ width: '35%', backgroundColor: '#3b82f6' }} title="Email 35%" />
          <div className="dist-segment" style={{ width: '25%', backgroundColor: '#10b981' }} title="Push 25%" />
          <div className="dist-segment" style={{ width: '20%', backgroundColor: '#8b5cf6' }} title="SMS 20%" />
          <div className="dist-segment" style={{ width: '20%', backgroundColor: '#f59e0b' }} title="Ads 20%" />
        </div>
        <div className="dist-legend">
          <span><i style={{backgroundColor: '#3b82f6'}}></i>Email</span>
          <span><i style={{backgroundColor: '#10b981'}}></i>Push</span>
          <span><i style={{backgroundColor: '#8b5cf6'}}></i>SMS</span>
          <span><i style={{backgroundColor: '#f59e0b'}}></i>Ads</span>
        </div>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-section-zeta" data-testid="hero-section">
      <div 
        className="hero-bg-image" 
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/hero-bg.jpg)` }}
      />
      <div className="hero-bg-overlay" />
      <div className="hero-content-zeta">
        <div className="hero-text-zeta">
          <span className="hero-tagline-zeta" data-testid="hero-tagline">Data Science. Precision Marketing.</span>
          <h1 className="hero-title-zeta" data-testid="hero-title">
            Performance marketing powered by <span className="highlight-zeta">intelligence</span>
          </h1>
          <p className="hero-description-zeta" data-testid="hero-description">
            We combine tens of millions of consumer profiles with advanced analytics and AI 
            to identify, reach, and convert your ideal customers. Precise targeting. 
            Measurable outcomes. Predictable growth.
          </p>
          <div className="hero-cta-zeta">
            <Link to="/how-it-works">
              <Button 
                className="btn-primary-zeta"
                data-testid="hero-cta-primary"
              >
                See How It Works
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Marquee Section
const MarqueeSection = () => {
  return (
    <section className="marquee-section" data-testid="marquee-section">
      <p className="marquee-label">Trusted by Brands You Know</p>
      <div className="marquee-wrapper">
        <div className="marquee-track">
          {[...partners, ...partners].map((partner, index) => (
            <span key={index} className="marquee-item">{partner}</span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Content Section 1 - AI Made Accessible
const ContentSection1 = () => {
  const bulletPoints = [
    { highlight: "245M+ consumer profiles", text: "with behavioral, transactional, and intent data." },
    { highlight: "Billions of intent signals", text: "enable precise audience identification and targeting." },
    { highlight: "Zero integration required", text: "—our data is ready for immediate campaign activation." },
    { highlight: "Cross-platform identity resolution", text: "connects consumers across devices and channels." }
  ];

  return (
    <section className="content-section" id="platform" data-testid="content-section-1">
      <div className="content-wrapper">
        <div className="content-visual-left">
          <AISignalVisualization />
        </div>
        
        <div className="content-text-right">
          <span className="section-eyebrow">Meet IRIS</span>
          <h2>Proprietary Data. Predictive Intelligence.</h2>
          <p className="content-description">
            IRIS—our Intelligent Real-time Intent System—analyzes our proprietary database 
            of 245+ million consumers to identify high-value prospects and predict 
            optimal engagement strategies with measurable precision.
          </p>
          
          <div className="bullet-list">
            {bulletPoints.map((point, index) => (
              <div key={index} className="bullet-item" data-testid={`bullet-1-${index}`}>
                <div className="bullet-bar"></div>
                <p><strong>{point.highlight}</strong> {point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Content Section 2 - Unified Profiles
const ContentSection2 = () => {
  const bulletPoints = [
    { highlight: "Predictive modeling", text: "determines optimal creative, timing, and channel for each segment." },
    { highlight: "Premium inventory access", text: "ensures quality placements across verified publisher networks." },
    { highlight: "Dynamic optimization", text: "reallocates budget based on real-time performance signals." },
    { highlight: "Attribution modeling", text: "provides clear visibility into cross-channel contribution." }
  ];

  return (
    <section className="content-section alt-bg" id="activation" data-testid="content-section-2">
      <div className="content-wrapper">
        <div className="content-text-left">
          <span className="section-eyebrow">Proprietary Data</span>
          <h2>Comprehensive Consumer Intelligence</h2>
          <p className="content-description">
            Our database aggregates behavioral, transactional, and demographic signals 
            into unified consumer profiles. This enables precise segmentation and 
            targeting strategies that drive measurable campaign performance.
          </p>
          
          <div className="bullet-list">
            {bulletPoints.map((point, index) => (
              <div key={index} className="bullet-item" data-testid={`bullet-2-${index}`}>
                <div className="bullet-bar"></div>
                <p><strong>{point.highlight}</strong> {point.text}</p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="content-visual-right">
          <ProfileUnificationVisualization />
        </div>
      </div>
    </section>
  );
};

// Content Section 3 - Cross-channel Engagement
const ContentSection3 = () => {
  const features = [
    { icon: BarChart3, title: "Full-Funnel Analytics", desc: "Track performance from awareness through conversion." },
    { icon: Users, title: "Incrementality Testing", desc: "Measure true campaign lift with controlled experiments." },
    { icon: Globe, title: "Multi-Touch Attribution", desc: "Understand channel contribution across the journey." }
  ];

  return (
    <section className="content-section" data-testid="content-section-3">
      <div className="content-wrapper">
        <div className="content-visual-left">
          <OrchestrationVisualization />
        </div>
        
        <div className="content-text-right">
          <span className="section-eyebrow">Omnichannel</span>
          <h2>Coordinated Cross-Channel Execution</h2>
          <p className="content-description">
            Our orchestration engine coordinates messaging across email, SMS, push, 
            in-app, and paid media—ensuring consistent, personalized experiences 
            that align with each consumer's journey stage and preferences.
          </p>
          
          <p className="content-subtitle">Every campaign includes:</p>
          
          <div className="feature-list">
            {features.map((feature, index) => (
              <div key={index} className="feature-item" data-testid={`feature-3-${index}`}>
                <div className="feature-icon-small">
                  <feature.icon size={20} />
                </div>
                <div>
                  <strong>{feature.title}</strong> — {feature.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Omnichannel Section
const OmniSection = () => {
  return (
    <section className="omni-section-zeta" id="channels" data-testid="omni-section">
      <div className="omni-content-zeta">
        <div className="section-header-zeta">
          <span className="section-label-zeta">Multi-Channel Reach</span>
          <h2>Unified Activation Across Every Channel</h2>
          <p>
            Our platform enables coordinated campaign execution across all major 
            channels, with consistent measurement and optimization.
          </p>
        </div>
        
        <div className="channels-grid-zeta">
          {channels.map((channel, index) => (
            <div 
              key={index} 
              className="channel-card-zeta"
              data-testid={`channel-card-${index}`}
            >
              <div className="channel-icon-zeta">
                <channel.icon size={24} />
              </div>
              <span className="channel-name-zeta">{channel.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="stats-section-zeta" ref={sectionRef} data-testid="stats-section">
      <div className="stats-grid-zeta">
        {stats.map((stat, index) => (
          <div 
            key={index} 
            className={`stat-card-zeta ${isVisible ? "visible" : ""}`}
            data-testid={`stat-card-${index}`}
          >
            <div className="stat-value-zeta">{stat.value}</div>
            <div className="stat-label-zeta">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className="testimonials-section-zeta" id="results" data-testid="testimonials-section">
      <div className="section-header-zeta">
        <span className="section-label-zeta">Case Studies</span>
        <h2>Measured Outcomes Across Industries</h2>
        <p>Documented results from campaigns powered by our data and methodology.</p>
      </div>
      
      <div className="testimonials-grid-zeta">
        {caseStudies.map((study, index) => (
          <div key={index} className="testimonial-card-zeta" data-testid={`testimonial-card-${index}`}>
            <img src={study.image} alt={study.category} className="testimonial-image-zeta" />
            <div className="testimonial-content-zeta">
              <span className="testimonial-category-zeta">{study.category}</span>
              <div className="testimonial-goal-zeta">
                <strong>Objective:</strong> {study.goal}
              </div>
              <div className="testimonial-result-zeta">
                <strong>Outcome:</strong> {study.result}
              </div>
              <Link to={`/case-study/${study.slug}`} className="testimonial-link-zeta">
                View Full Analysis <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="cta-section-zeta" id="cta" data-testid="cta-section">
      <div className="cta-content-zeta">
        <h2>Let's Discuss Your Growth Objectives</h2>
        <p>
          Schedule a consultation to learn how our proprietary data and 
          analytical approach can drive measurable results for your business.
        </p>
        <Link to="/contact">
          <Button 
            className="btn-primary-zeta"
            data-testid="cta-button"
          >
            Schedule Consultation
            <ArrowRight size={18} />
          </Button>
        </Link>
      </div>
    </section>
  );
};

// Main Home Page Component
function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEO 
        title="Performance Marketing Powered by Intelligence"
        description="IntentLogic combines tens of millions of consumer profiles with advanced analytics and AI to identify, reach, and convert your ideal customers. Precise targeting. Measurable outcomes. Predictable growth."
        keywords="performance marketing, predictive marketing, consumer data, AI marketing, customer acquisition, intent data, marketing analytics, data-driven marketing, programmatic advertising, customer intelligence"
        canonical="/"
      />
      <HeroSection />
      <MarqueeSection />
      <ContentSection1 />
      <ContentSection2 />
      <ContentSection3 />
      <OmniSection />
      <TestimonialsSection />
      <CTASection />
    </Layout>
  );
}

export default HomePage;
